import { create } from 'zustand';
import odooClient from '../services/odooClient';
import { getArtworkImageCandidates, normalizeArtworkImageValue } from '../utils/artworkImages';

export interface Artwork {
  id: string;
  IdName?: string; // Artwork ID from Odoo (display-preferred)
  LtdNumber?: string;
  title: string;
  artist: string;
  year: number;
  medium: string;
  dimensions: string;
  price: number;
  currency: string;
  category: string;
  status: string;
  ownership?: string;
  location: string;

  subLocation: string;

  image_url?: string;
  image_candidates?: string[];
  created_at: string;
  type: string;
  dateInStock: string;
  maxDiscount: number;
  sizeCm: string;
  sizeH: number;
  sizeL: number;
  sizeInch: string;
  weight: string;
  region: string;
  currencyRef?: string;
  pdfFiles?: any;
  videoFiles?: any;
  sizeP?: number;
  artwork_reserved?: string;
  artwork_reserved_gallery?: string;
  artwork_reserved_user?: string;
  artwork_reserved_date?: any;
  x_auction_house_date?: string | null;


  prices?: {
    CHF?: number;
    EUR?: number;
    GBP?: number;
    HKD?: number;
    KRW?: number;
    AED?: number;
    USD?: number;
    SGD?: number;
  };

  NewMainLR?: string;
  NewMainHR?: string;
  main_super_picture_hd?: string;
  other_url?: string;
  detail_1_url?: string;
  detail_2_url?: string;
  main_picture_hd?: string;
  back_pictures?: string;
  main_web_picture?: string;
  frame_picture?: string;
  main_a6_picture?: string;
  main_hd_web_picture?: string;
  perspective_url?: string;
  signatures_pictures?: string;

  // 📜 Certificate & Provenance Fields (for artwork detail view)
  CertificateWording?: string;
  ProvenanceWordingCrm?: string;
  CertificateCertAddress?: string;
  artwork_exhibited?: string;
  artwork_signed?: string;
  LiteratureEn?: string;
  certificateid?: number | string;
  certificateurl?: string;
  VideoPresentationUrl1?: string;
  VideoPresentationUrl2?: string;
  VideoDetailUrl1?: string;
  VideoDetailUrl2?: string;
  VideoInstallationUrl1?: string;
  VideoInstallationUrl2?: string;
  VideoTimelapseUrl1?: string;
  VideoTimelapseUrl2?: string;
  VideoDocumentaryUrl1?: string;
  VideoDocumentaryUrl2?: string;
  VideoInterviewUrl1?: string;
  VideoInterviewUrl2?: string;
  CatalogueUrl1?: string;
  CatalogueUrl2?: string;
  CertificateUrl1?: string;
  CertificateUrl2?: string;
  PublicationUrl1?: string;
  PublicationUrl2?: string;
  OtherDocumentUrl1?: string;
  OtherDocumentUrl2?: string;

  is_shared_property?: string;

}

// Helper functions (can be moved to a utils file later)
const formatArtworkDimensions = (dims: { height?: number; width?: number; depth?: number }): string => {
  if (!dims.height || !dims.width) return 'Dimensions not available';
  return `${dims.height} x ${dims.width} ${dims.depth ? `x ${dims.depth}` : ''} cm`;
};

const mapOperaCRMStatus = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    'In stock': 'In Stock',
    'Available': 'In Stock',
    'Reserved': 'On Option',
    'On order unpaid': 'On Hold',
    'On order in payment': 'On Hold',
    'Sold': 'Sold',
  };
  return statusMap[status] || status || 'Unknown';
};

// Helper function to safely extract image URL from Odoo (handles false, null, empty string)
const safeImageUrl = (value: any): string => normalizeArtworkImageValue(value);

const parseBooleanLike = (value: unknown): boolean | undefined => {
  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    if (['1', 'true', 'yes'].includes(normalizedValue)) {
      return true;
    }
    if (['0', 'false', 'no'].includes(normalizedValue)) {
      return false;
    }
  }

  if (value === 1 || value === '1' || value === true) {
    return true;
  }

  if (value === 0 || value === '0' || value === false) {
    return false;
  }

  return undefined;
};

const resolveOwnershipLabel = (artwork: any): string | undefined => {
  const rawConsPurchase = artwork?.ConsPurchase;
  const rawOwnerShip =
    artwork?.OwnerShip ?? artwork?.ownership ?? artwork?.Ownership;
  const rawConsignmentBoolean =
    artwork?.ConsignmentCustomer ??
    artwork?.ConsPurchaseandConsignmentCustomer ??
    artwork?.ConsPurchaseand_ConsignmentCustomer ??
    artwork?.cons_purchaseand_consignment_customer;

  const isConsignmentCustomer = parseBooleanLike(rawConsignmentBoolean);
  const normalizedSecondaryOwnership =
    isConsignmentCustomer === true
      ? '3RDP'
      : isConsignmentCustomer === false
        ? undefined
        : typeof rawConsignmentBoolean === 'string' && rawConsignmentBoolean.trim()
          ? rawConsignmentBoolean.trim()
          : undefined;

  const primaryOwnership =
    typeof rawConsPurchase === 'string' && rawConsPurchase.trim()
      ? rawConsPurchase.trim()
      : typeof rawOwnerShip === 'string' && rawOwnerShip.includes('/')
        ? rawOwnerShip.split('/')[0]?.trim() || undefined
        : undefined;

  if (typeof rawOwnerShip === 'string' && rawOwnerShip.trim()) {
    const trimmedOwnerShip = rawOwnerShip.trim();
    const normalizedOwnerShipBoolean = parseBooleanLike(trimmedOwnerShip);
    if (normalizedOwnerShipBoolean === true) return '3RDP';
    if (normalizedOwnerShipBoolean === false) return undefined;

    if (primaryOwnership && normalizedSecondaryOwnership) {
      return `${primaryOwnership} / ${normalizedSecondaryOwnership}`;
    }

    if (primaryOwnership) {
      return primaryOwnership;
    }

    if (normalizedSecondaryOwnership) {
      return normalizedSecondaryOwnership;
    }

    return trimmedOwnerShip;
  }

  if (primaryOwnership && normalizedSecondaryOwnership) {
    return `${primaryOwnership} / ${normalizedSecondaryOwnership}`;
  }
  if (primaryOwnership) return primaryOwnership;
  if (normalizedSecondaryOwnership) return normalizedSecondaryOwnership;
  return undefined;
};

export const formatArtworkData = (artwork: any): Artwork => {

  const prices: Artwork['prices'] = {};


  const extractPrice = (value: any): number | undefined => {
    if (value === null || value === undefined || value === false || value === '') return undefined;
    const num = Number(value);
    return isNaN(num) || num <= 0 ? undefined : num;
  };

  const chfPrice = extractPrice(artwork.PriceCHFEnt);
  const eurPrice = extractPrice(artwork.PriceEUREnt);
  const gbpPrice = extractPrice(artwork.PriceGBPEnt);
  const hkdPrice = extractPrice(artwork.PriceHKDEnt);
  const krwPrice = extractPrice(artwork.PriceKRWEnt);
  const aedPrice = extractPrice(artwork.PriceAEDEnt);
  const usdPrice = extractPrice(artwork.PriceUSDEnt);
  const sgdPrice = extractPrice(artwork.PriceSGDEnt);

  if (chfPrice !== undefined) prices.CHF = chfPrice;
  if (eurPrice !== undefined) prices.EUR = eurPrice;
  if (gbpPrice !== undefined) prices.GBP = gbpPrice;
  if (hkdPrice !== undefined) prices.HKD = hkdPrice;
  if (krwPrice !== undefined) prices.KRW = krwPrice;
  if (aedPrice !== undefined) prices.AED = aedPrice;
  if (usdPrice !== undefined) prices.USD = usdPrice;
  if (sgdPrice !== undefined) prices.SGD = sgdPrice;


  if (!(window as any).__artworkDebugCount) {
    (window as any).__artworkDebugCount = 0;
  }
  if ((window as any).__artworkDebugCount < 3) {
    (window as any).__artworkDebugCount++;
    console.log(`🔍 Artwork #${(window as any).__artworkDebugCount} price data check:`, {
      id: artwork.id,
      hasPrices: Object.keys(prices).length > 0,
      prices: prices,
      rawFields: {
        PriceCHFEnt: artwork.PriceCHFEnt,
        PriceEUREnt: artwork.PriceEUREnt,
        PriceGBPEnt: artwork.PriceGBPEnt,
        PriceUSDEnt: artwork.PriceUSDEnt,
        PriceHKDEnt: artwork.PriceHKDEnt,
        PriceKRWEnt: artwork.PriceKRWEnt,
        PriceAEDEnt: artwork.PriceAEDEnt,
        PriceSGDEnt: artwork.PriceSGDEnt,
      },
      extractedPrices: {
        CHF: chfPrice,
        EUR: eurPrice,
        GBP: gbpPrice,
        USD: usdPrice,
        AED: aedPrice,
      },
      legacyPrice: artwork.artwork_price_combined || artwork.PriceRef,
      legacyCurrency: artwork?.CurrencyRef || artwork?.currencyRef
    });
  }


  const legacyPrice = artwork.artwork_price_combined || artwork.PriceRef || 0;
  const legacyCurrency = artwork?.CurrencyRef || artwork?.currencyRef || "EUR";

  const imageCandidates = getArtworkImageCandidates(artwork);

  return {
    id: artwork.id.toString(),
    IdName: artwork.IdName || undefined,
    LtdNumber:
      typeof artwork.LtdNumber === 'string' ? artwork.LtdNumber.trim() : undefined,
    title: artwork.name || 'Untitled',
    artist: Array.isArray(artwork.res_artist_id) ? artwork.res_artist_id[1] || 'Unknown Artist' : 'Unknown Artist',
    year: artwork.artwork_year || (artwork.entry_date ? new Date(artwork.entry_date).getFullYear() : new Date().getFullYear()),
    medium: artwork.Medium || 'Mixed media',
    dimensions: formatArtworkDimensions({ height: artwork.SizeH, width: artwork.SizeL, depth: artwork.SizeP }),
    price: legacyPrice,
    currency: legacyCurrency,
    currencyRef: artwork?.CurrencyRef || artwork?.currencyRef,
    pdfFiles: artwork?.pdfFiles,
    videoFiles: artwork?.videoFiles,
    prices: Object.keys(prices).length > 0 ? prices : undefined,
    category: artwork.Category || 'Contemporary',
    status: mapOperaCRMStatus(artwork.Status),
    ownership: resolveOwnershipLabel(artwork),
    location: artwork.Location || artwork.control_gallery_combined || 'Art Gallery',

    subLocation: artwork.SubLocation,

    type: artwork.Type || 'Unknown',
    dateInStock: artwork.entry_date || 'N/A',
    maxDiscount: artwork.ApprouvedDiscount || 0,
    sizeCm: artwork.SizeL && artwork.SizeH ? `${artwork.SizeH} cm / ${artwork.SizeL} cm` : "-",
    sizeH: artwork.SizeH || 0,
    sizeL: artwork.SizeL || 0,
    sizeInch: `${artwork.SizeIncH || 0} in / ${artwork.SizeIncL || 0} in`,
    weight: `${artwork.WeightKg || 0} Kg / ${artwork.WeightPounds || 0} lb`,
    region: artwork.Region || 'N/A',
    x_auction_house_date: artwork.x_auction_house_date || null,
    image_url: imageCandidates[0] || '',
    image_candidates: imageCandidates,
    created_at: artwork.entry_date || '',

    // ✅ INCLUDE ALL 11 IMAGE URL FIELDS from Odoo (safe handling of mixed types)
    NewMainLR: safeImageUrl(artwork.NewMainLR),
    NewMainHR: safeImageUrl(artwork.NewMainHR),
    main_super_picture_hd: safeImageUrl(artwork.main_super_picture_hd),
    other_url: safeImageUrl(artwork.other_url),
    detail_1_url: safeImageUrl(artwork.detail_1_url),
    detail_2_url: safeImageUrl(artwork.detail_2_url),
    main_picture_hd: safeImageUrl(artwork.main_picture_hd),
    back_pictures: safeImageUrl(artwork.back_pictures),
    main_web_picture: safeImageUrl(artwork.main_web_picture),
    frame_picture: safeImageUrl(artwork.frame_picture),
    main_a6_picture: safeImageUrl(artwork.main_a6_picture),
    main_hd_web_picture: safeImageUrl(artwork.main_hd_web_picture),
    perspective_url: safeImageUrl(artwork.perspective_url),
    signatures_pictures: safeImageUrl(artwork.signatures_pictures),
    VideoPresentationUrl1: safeImageUrl(artwork.VideoPresentationUrl1),
    VideoPresentationUrl2: safeImageUrl(artwork.VideoPresentationUrl2),
    VideoDetailUrl1: safeImageUrl(artwork.VideoDetailUrl1),
    VideoDetailUrl2: safeImageUrl(artwork.VideoDetailUrl2),
    VideoInstallationUrl1: safeImageUrl(artwork.VideoInstallationUrl1),
    VideoInstallationUrl2: safeImageUrl(artwork.VideoInstallationUrl2),
    VideoTimelapseUrl1: safeImageUrl(artwork.VideoTimelapseUrl1),
    VideoTimelapseUrl2: safeImageUrl(artwork.VideoTimelapseUrl2),
    VideoDocumentaryUrl1: safeImageUrl(artwork.VideoDocumentaryUrl1),
    VideoDocumentaryUrl2: safeImageUrl(artwork.VideoDocumentaryUrl2),
    VideoInterviewUrl1: safeImageUrl(artwork.VideoInterviewUrl1),
    VideoInterviewUrl2: safeImageUrl(artwork.VideoInterviewUrl2),
    CatalogueUrl1: safeImageUrl(artwork.CatalogueUrl1),
    CatalogueUrl2: safeImageUrl(artwork.CatalogueUrl2),
    CertificateUrl1: safeImageUrl(artwork.CertificateUrl1),
    CertificateUrl2: safeImageUrl(artwork.CertificateUrl2),
    PublicationUrl1: safeImageUrl(artwork.PublicationUrl1),
    PublicationUrl2: safeImageUrl(artwork.PublicationUrl2),
    OtherDocumentUrl1: safeImageUrl(artwork.OtherDocumentUrl1),
    OtherDocumentUrl2: safeImageUrl(artwork.OtherDocumentUrl2),
  };
};

interface ArtworkState {
  artworks: Artwork[];
  displayedArtworks: Artwork[];
  totalCount: number;
  currentPage: number; // New: Track current page
  itemsPerPage: number; // New: Track items per page
  loading: boolean;
  backgroundLoading: boolean;
  isFetching: boolean; // The "Do Not Disturb" flag
  loadArtworks: (page?: number, itemsPerPage?: number, force?: boolean) => Promise<void>;
  startBackgroundLoading: () => Promise<void>;
  searchArtworks: (searchTerm: string, filters?: any) => Promise<Artwork[]>; // New: Search method
  getDisplayedArtworks: () => Artwork[]; // Method to get stable artworks for UI
  setArtworks: (artworks: Artwork[], totalCount?: number) => void; // New: Set artworks directly
}

export const useArtworkStore = create<ArtworkState>((set, get) => ({
  artworks: [],
  displayedArtworks: [],
  totalCount: 0,
  currentPage: 1,      // New: Initialize current page
  itemsPerPage: 20,    // Changed: Use 20 to match contacts
  loading: false,
  backgroundLoading: false,
  isFetching: false,



  loadArtworks: async (page = 1, itemsPerPage = 20, forceReload = false) => {
    const state = get();

    // Skip if already loading or if data exists and not forcing reload
    if (state.isFetching) {
      return;
    }

    if (!forceReload && state.artworks.length > 0 && state.currentPage === page) {
      return;
    }

    set({ isFetching: true, loading: true });

    try {
      // Calculate offset for this page
      const offset = (page - 1) * itemsPerPage;

      // Fetch this page of artworks AND total count simultaneously
      const [response, totalCount] = await Promise.all([
        odooClient.getArtworks(itemsPerPage, offset),
        page === 1 ? odooClient.getArtworksCount() : Promise.resolve(state.totalCount) // Only fetch count on first page
      ]);

      if (response && Array.isArray(response)) {
        const formatted = response.map(formatArtworkData);

        // Set artworks, displayedArtworks AND page info immediately
        set({
          artworks: formatted,
          displayedArtworks: formatted, // Keep UI stable
          totalCount: totalCount || state.totalCount,
          currentPage: page,
          itemsPerPage: itemsPerPage,
          loading: false
        });
      } else {
        set({ loading: false });
      }
    } catch (error) {
      console.error('❌ Failed to load artworks:', error);
      set({ loading: false });
    } finally {
      set({ isFetching: false });
    }
  },

  getDisplayedArtworks: () => {
    // Return displayedArtworks for UI, or first batch if displayedArtworks is empty
    const state = get();
    return state.displayedArtworks.length > 0 ? state.displayedArtworks : state.artworks.slice(0, 25);
  },

  searchArtworks: async (searchTerm: string, filters: any = {}) => {
    try {
      const response = await odooClient.searchArtworksWithFilters({
        searchTerm,
        ...filters
      }, 8, 0); // Limit to 8 suggestions
      if (response && Array.isArray(response)) {
        const formatted = response.map(formatArtworkData);
        return formatted;
      } else {
        return [];
      }
    } catch (error) {
      console.error('❌ Failed to search artworks:', error);
      return [];
    }
  },

  startBackgroundLoading: async () => {
    set({ backgroundLoading: true });
    let currentOffset = get().artworks.length;
    const batchSize = 100;

    try {
      while (true) {
        const batchResponse = await odooClient.getArtworks(batchSize, currentOffset);
        if (batchResponse && Array.isArray(batchResponse) && batchResponse.length > 0) {
          const formatted = batchResponse.map(formatArtworkData);
          // Only update the full artworks array, NOT displayedArtworks
          // This prevents UI flicker while still loading data in background
          set(state => ({
            artworks: [...state.artworks, ...formatted]
            // displayedArtworks stays unchanged - no UI updates!
          }));
          currentOffset += batchResponse.length;
          if (batchResponse.length < batchSize) break;
          await new Promise(resolve => setTimeout(resolve, 200));
        } else {
          break;
        }
      }
    } catch (error) {
      console.error('Failed to load remaining artworks:', error);
    } finally {
      set({ backgroundLoading: false });
    }
  },

  setArtworks: (artworks: Artwork[], totalCount?: number) => {
    set({
      artworks,
      displayedArtworks: artworks,
      totalCount: (typeof totalCount === 'number') ? totalCount : artworks.length
    });
  },
})); 
